import { Calendar } from 'lucide-react';
import DraftIcon from '../../../assets/icons/draft-icon.tsx';
import { NavChart } from '../../../assets/icons/nav-chart.tsx';
import { NavCustomer } from '../../../assets/icons/nav-customer.tsx';
import { NavHome } from '../../../assets/icons/nav-home.tsx';
import { NavTask } from '../../../assets/icons/nav-task.tsx';
import { OrderIcon } from '../../../assets/icons/order-icon.tsx';
import { QuoteIcon } from '../../../assets/icons/quote-icon.tsx';
import TransactionIcon from '../../../assets/icons/transaction-icon.tsx';
import type { MenuGroup, MenuItem, MenuSubItem } from '../contracts.ts';
import { NavPathOverrideEnum } from '../contracts.ts';
import type { BuildNavigationOptions } from './build-navigation-types.ts';

type RawMenuItem = Omit<MenuItem, 'subItems'> & {
    subItems?: (MenuSubItem | undefined | false)[];
};

/**
 * The flat rep menu — today's items in today's order with today's
 * post-processing (single-subitem collapse, draft gate, flag gates),
 * wrapped in a single unlabelled group so it renders byte-for-byte
 * identical to the pre-group-era menu.
 *
 * The manager-only analytics pages (Team, Activities) live exclusively in
 * Manager view, so the flat rep menu never includes them.
 */
export const buildRepNavigation = (options: BuildNavigationOptions): MenuGroup[] => {
    const { t, taskCount, appConfig } = options;
    const { canReadOrders, canReadQuotes, transactionDraftAvailability, enableCustomerEventPlanning } =
        appConfig;

    const items: (RawMenuItem | undefined | false)[] = [
        {
            id: NavPathOverrideEnum.HOME,
            name: t('page_title__home'),
            href: '/home',
            icon: NavHome,
        },
        enableCustomerEventPlanning && {
            id: NavPathOverrideEnum.EVENTS,
            name: t('page_title__events'),
            href: '/events',
            icon: Calendar,
        },
        {
            id: NavPathOverrideEnum.CUSTOMERS,
            name: t('page_title__customers'),
            href: '/customers',
            icon: NavCustomer,
        },
        {
            id: NavPathOverrideEnum.TASKS,
            name: t('page_title__tasks'),
            href: '/tasks',
            icon: NavTask,
            count: taskCount,
        },
        {
            id: NavPathOverrideEnum.EXPLORER,
            name: t('page_title__explorer'),
            href: '/explorer',
            icon: NavChart,
        },
        {
            id: NavPathOverrideEnum.TRANSACTIONS,
            name: t('page_title__transactions'),
            icon: TransactionIcon,
            subItems: [
                canReadQuotes && {
                    id: NavPathOverrideEnum.SALES_QUOTES,
                    name: t('page_title__sales_quotes'),
                    href: '/quotes',
                    icon: QuoteIcon,
                },
                canReadOrders && {
                    id: NavPathOverrideEnum.SALES_ORDERS,
                    name: t('page_title__sales_orders'),
                    href: '/orders',
                    icon: OrderIcon,
                },
                transactionDraftAvailability !== 'UNAVAILABLE' && {
                    id: NavPathOverrideEnum.SALES_DRAFTS,
                    name: t('page_title__sales_drafts'),
                    href: '/drafts',
                    icon: DraftIcon,
                },
            ],
        },
    ];

    const processedItems = items
        .map((item): MenuItem | undefined => {
            if (!item) {
                return undefined;
            }

            if (!item.subItems) {
                return { ...item, subItems: undefined };
            }

            const subItems = item.subItems.filter((i): i is MenuSubItem => Boolean(i));
            const [firstSubItem, ...restSubItems] = subItems;

            if (!firstSubItem) {
                // Every sub-item was gated out — drop the parent entirely.
                return undefined;
            }

            if (restSubItems.length === 0) {
                // A single surviving sub-item collapses into a top-level link,
                // inheriting the parent's count.
                return { ...firstSubItem, count: item.count };
            }

            return { ...item, subItems };
        })
        .filter((i): i is MenuItem => Boolean(i));

    return [
        {
            id: 'launchpad',
            items: processedItems,
        },
    ];
};
